/* Solicitudes a GafCORE (traslados de activos): Solicitudes > GafCORE.
 *
 * api-central es un pasamanos hacia EMSYS, que guarda la solicitud y habla con
 * GafCORE (donde la atiende GAF). Las reglas de cada tipo (si lleva equipos,
 * desde, hasta) vienen en el catalogo de EMSYS: la app no las repite.
 *
 * Pide cualquier cargo menos los operarios. La app solo oculta la opcion; EMSYS
 * lo decide (permiso) y lo vuelve a controlar al crear.
 */
import api from "src/services/api";

const BASE = "traslados";

interface ApiOk<T> {
  ok: boolean;
  data: T;
}

/** "si" obligatorio, "no" no lleva, "opc" opcional. */
export type ReglaCampo = "si" | "no" | "opc";

export interface TipoTraslado {
  valor: string;
  etiqueta: string;
  equipos: ReglaCampo;
  desde: ReglaCampo;
  hasta: ReglaCampo;
}

export interface CatalogoTraslados {
  tipos: TipoTraslado[];
  max_equipos: number;
  primera_fecha: string;
  situaciones: { valor: string; etiqueta: string; color: string }[];
}

/** Un lugar de GafCORE. Si es un PS nuestro, `codigo` es su objeto y `nombre` el del PS. */
export interface LugarGafcore {
  codigo: string;
  nombre: string;
  vigente: boolean;
  equipos?: number | null;
  es_ps: boolean;
}

export interface ActivoGafcore {
  codigo_g: string;
  cod_fracttal?: string | null;
  nombre: string;
  ubicacion_codigo?: string | null;
  ubicacion_nombre?: string | null;
  componente_de?: string | null;
  traslado_en_curso?: string | null;
  traslado_hacia?: string | null;
}

export interface SolicitudTrasladoResumen {
  id: number;
  referencia: string;
  gaf_codigo?: string | null;
  tipo: string;
  tipo_label: string;
  situacion: string;
  situacion_label: string;
  situacion_color: string;
  cerrado: boolean;
  origen?: string | null;
  destino?: string | null;
  fecha_requerida?: string | null;
  horario?: string | null;
  creado_en?: string | null;
  asignado_a?: string | null;
}

export interface EquipoTraslado {
  codigo_g: string;
  cod_fracttal?: string | null;
  nombre?: string | null;
  situacion: string;
  situacion_label: string;
  situacion_color: string;
  traslado?: string | null;
  salida_en?: string | null;
  llegada_en?: string | null;
  motivo?: string | null;
}

export interface SolicitudTrasladoDetalle extends SolicitudTrasladoResumen {
  detalle: string;
  envio_estado: string;
  envio_error?: string | null;
  motivo_cierre?: string | null;
  origen_coincide?: boolean | null;
  origen_segun_gafcore?: string | null;
  equipos_pendientes?: number | null;
  hitos: { etiqueta: string; fecha: string | null }[];
  equipos_solicitados: { codigo: string; codigo_g?: string | null; cod_fracttal?: string | null; nombre?: string | null }[];
  equipos: EquipoTraslado[];
  sin_conexion: boolean;
}

export const TrasladosAPI = {
  permiso: () => api.get<ApiOk<{ puede_solicitar: boolean; mensaje?: string | null }>>(`${BASE}/permiso`),
  catalogo: () => api.get<ApiOk<CatalogoTraslados>>(`${BASE}/catalogo`),
  lugares: (q: string, soloVigentes = false) =>
    api.get<ApiOk<LugarGafcore[]>>(`${BASE}/lugares`, { params: { q, vigentes: soloVigentes ? "1" : "" } }),
  activos: (params: { q?: string; ubicacion?: string }) =>
    api.get<ApiOk<{ activos: ActivoGafcore[]; hay_mas: boolean }>>(`${BASE}/activos`, { params }),
  mis: () => api.get<ApiOk<SolicitudTrasladoResumen[]>>(`${BASE}/solicitudes`),
  // El alta espera la respuesta de GafCORE: mas margen que el timeout por defecto.
  crear: (payload: Record<string, any>) =>
    api.post<ApiOk<{ id: number; enviada: boolean; info: string }>>(`${BASE}/solicitudes`, payload, {
      timeout: 60000,
    }),
  detalle: (id: number) => api.get<ApiOk<SolicitudTrasladoDetalle>>(`${BASE}/solicitudes/${id}`),
};

/** "2026-10-08 14:30" -> "08/10/2026 14:30" */
export const fmtFechaHoraTexto = (f?: string | null) => {
  if (!f) return "-";
  const [d, h] = f.split(" ");
  const [a, m, dia] = d.split("-");
  return `${dia}/${m}/${a}${h ? ` ${h}` : ""}`;
};
